import type { ViewportType } from '~/types/elementsUi'
import { match } from 'ts-pattern'

export function useViewport() {
  const { $device } = useNuxtApp()
  let ssrWidth: number | undefined = undefined
  if (import.meta.server) {
    if ($device.isTablet) {
      ssrWidth = 1023
    } else if ($device.isDesktop) {
      ssrWidth = 1407
    } else {
      // Default to the most common screen size (mobile)
      ssrWidth = 768
    }
  }

  const breakpoints = useBreakpoints(
    {
      mobile: 768,
      tablet: 1023,
      desktop: 1215,
      widescreen: 1407,
      fullHD: 1650
    },
    { ssrWidth: ssrWidth }
  )

  const currentBreakpoint = computed<ViewportType>(() => {
    const active = breakpoints.active()
    return match<'' | ViewportType, ViewportType>(active.value)
      .with('', () => 'mobile')
      .with('mobile', () => 'tablet')
      .with('tablet', () => 'desktop')
      .with('desktop', () => 'widescreen')
      .with('widescreen', 'fullHD', () => 'fullHD')
      .exhaustive()
  })

  return {
    // Recreate the same API as nuxt-viewport
    isLessThan: (breakpoint: ViewportType) => {
      return breakpoints.smaller(breakpoint).value
    },
    isGreaterThan: (breakpoint: ViewportType) => {
      return breakpoints.greater(breakpoint).value
    },
    isGreaterOrEqual: (breakpoint: ViewportType) => {
      return breakpoints.greaterOrEqual(breakpoint).value
    },
    isLessOrEqual: (breakpoint: ViewportType) => {
      return breakpoints.smallerOrEqual(breakpoint).value
    },
    // Direct access to the current breakpoint
    current: currentBreakpoint,
    // Direct access to breakpoint checkers
    isMobile: breakpoints.smaller('tablet'),
    isTablet: breakpoints.between('tablet', 'desktop'),
    isDesktop: breakpoints.greaterOrEqual('desktop')
  }
}
